/**
 * AA Callback Hooks
 *
 * React hooks for processing AA (Account Aggregator) callback from Saafe redirect.
 * Parses URL parameters, manages callback state, and provides navigation utilities.
 *
 * **IMPORTANT: External Redirect Context**
 * These hooks are designed to run after a fresh page load triggered by an external
 * redirect from Saafe (the AA consent provider). The callback parameters are parsed
 * from `window.location.search` because they come from an external redirect, not
 * from client-side navigation.
 *
 * @warning **Client-Side Navigation Limitation**
 * The `useAACallbackParams` hook reads URL parameters on initial mount only.
 * It will NOT react to client-side navigation changes (e.g., using TanStack Router's
 * `navigate()` or `<Link>`). This is intentional because AA callbacks always come
 * from external browser redirects, not internal SPA navigation.
 *
 * @example
 * ```tsx
 * function AACallbackHandler() {
 *   const {
 *     hasCallback,
 *     callbackResult,
 *     isSuccess,
 *     isError,
 *     isRetrying,
 *     clearCallbackParams,
 *     navigateToPortfolio,
 *   } = useAACallback();
 *
 *   if (!hasCallback) return null;
 *
 *   if (isSuccess) {
 *     return <SuccessScreen linkedAccounts={callbackResult.linkedAccountsCount} />;
 *   }
 *
 *   if (isError) {
 *     return <ErrorScreen message={callbackResult.error} />;
 *   }
 * }
 * ```
 */

import { useMemo, useCallback, useEffect, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  parseAACallbackParams,
  hasAACallbackParams,
  removeAACallbackParams,
  getAAStatus,
  getAAErrorMessage,
  aaQueryKeys,
  type AACallbackParams,
  type AAStatus,
} from "@/lib/aa-api";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { FileRouteTypes } from "@/routeTree.gen";

// ============================================================================
// Types
// ============================================================================

/**
 * Valid route paths from TanStack Router's generated route tree.
 * Used for type-safe navigation in the AA callback hooks.
 */
export type ValidRoutePath = FileRouteTypes["fullPaths"];

/**
 * Callback processing state
 */
export type CallbackState = "idle" | "processing" | "success" | "error";

/**
 * Return type for useAACallbackParams hook
 */
export interface UseAACallbackParamsReturn {
  /** Whether URL contains AA callback parameters */
  hasCallback: boolean;
  /** Parsed callback result from URL params */
  callbackResult: AACallbackParams;
  /** Remove AA callback params from URL without navigation */
  clearCallbackParams: () => void;
}

/**
 * Return type for useAAStatusFetch hook
 */
export interface UseAAStatusFetchReturn {
  /** Updated AA status after successful fetch (from BFF) */
  aaStatus: AAStatus | null;
  /** Error during status fetch (if any) */
  statusError: Error | null;
  /** Whether we're currently fetching status */
  isFetching: boolean;
  /** Whether this is a retry attempt (not the initial fetch) */
  isRetrying: boolean;
  /** Retry fetching AA status */
  retry: () => void;
}

/**
 * Return type for useAACallback hook
 */
export interface UseAACallbackReturn {
  /** Whether URL contains AA callback parameters */
  hasCallback: boolean;
  /** Parsed callback result from URL params */
  callbackResult: AACallbackParams;
  /** Current processing state */
  state: CallbackState;
  /** Whether callback was successful */
  isSuccess: boolean;
  /** Whether callback resulted in error */
  isError: boolean;
  /** Whether we're still processing the callback */
  isProcessing: boolean;
  /** Whether callback is idle (no callback params) */
  isIdle: boolean;
  /** Whether this is a retry attempt (not the initial fetch) */
  isRetrying: boolean;
  /** Number of retry attempts made */
  retryCount: number;
  /** Updated AA status after successful callback (fetched from BFF) */
  aaStatus: AAStatus | null;
  /** Error during status fetch (if any) */
  statusError: Error | null;
  /** User-friendly error message */
  errorMessage: string | null;
  /** Remove AA callback params from URL without navigation */
  clearCallbackParams: () => void;
  /** Navigate to portfolio/home page after callback */
  navigateToPortfolio: () => void;
  /**
   * Navigate to a specific path after callback.
   * Uses type-safe route paths from TanStack Router.
   */
  navigateTo: (path: ValidRoutePath) => void;
  /** Retry fetching AA status */
  retryStatusFetch: () => void;
}

/**
 * Options for useAACallback hook
 */
export interface UseAACallbackOptions {
  /**
   * Whether to automatically fetch updated AA status after successful callback.
   * Default: true
   */
  fetchStatusOnSuccess?: boolean;
  /**
   * Whether to automatically clear callback params from URL after processing.
   * Default: false (let the component decide when to clear)
   */
  autoClearParams?: boolean;
}

// ============================================================================
// useAACallbackParams Hook
// ============================================================================

/**
 * Hook for parsing AA callback parameters from the URL.
 *
 * **IMPORTANT: External Redirect Context**
 * This hook reads `window.location.search` directly because AA callbacks come
 * from external Saafe redirects, not client-side navigation. The parameters
 * are captured on initial mount and will NOT update on client-side navigation.
 *
 * @warning This hook is designed for processing external redirects only.
 * It will not react to client-side URL changes made via TanStack Router.
 *
 * @returns Parsed callback parameters and utilities
 *
 * @example
 * ```tsx
 * const { hasCallback, callbackResult, clearCallbackParams } = useAACallbackParams();
 *
 * if (hasCallback && callbackResult.status === 'success') {
 *   console.log('Linked accounts:', callbackResult.linkedAccountsCount);
 * }
 * ```
 */
export function useAACallbackParams(): UseAACallbackParamsReturn {
  // Capture search params once on mount - these come from external redirect
  // and won't change during the component lifecycle
  const [searchParams] = useState(() => {
    if (typeof window === "undefined") return "";
    return window.location.search;
  });

  const hasCallback = useMemo(
    () => hasAACallbackParams(searchParams),
    [searchParams]
  );

  const callbackResult = useMemo(
    () => parseAACallbackParams(searchParams),
    [searchParams]
  );

  // Clear callback params from URL
  const clearCallbackParams = useCallback(() => {
    const cleanParams = removeAACallbackParams(searchParams);
    const newSearch = cleanParams.toString();
    const newUrl = newSearch
      ? `${window.location.pathname}?${newSearch}`
      : window.location.pathname;
    window.history.replaceState({}, "", newUrl);
  }, [searchParams]);

  return {
    hasCallback,
    callbackResult,
    clearCallbackParams,
  };
}

// ============================================================================
// useAAStatusFetch Hook
// ============================================================================

/**
 * Hook for fetching AA status from the BFF using React Query.
 *
 * This hook manages the async fetch of AA status, including error handling
 * and retry logic. It only fetches when `enabled` is true.
 *
 * @param enabled - Whether to fetch AA status. Set to true after successful callback.
 * @returns AA status data, loading state, and retry function
 *
 * @example
 * ```tsx
 * const { hasCallback, callbackResult } = useAACallbackParams();
 * const shouldFetch = hasCallback && callbackResult.status === 'success';
 *
 * const { aaStatus, statusError, isFetching, isRetrying, retry } = useAAStatusFetch(shouldFetch);
 *
 * if (isFetching) return <Spinner />;
 * if (statusError) return <ErrorWithRetry onRetry={retry} isRetrying={isRetrying} />;
 * if (aaStatus) return <StatusDisplay status={aaStatus} />;
 * ```
 */
export function useAAStatusFetch(enabled: boolean): UseAAStatusFetchReturn {
  const queryClient = useQueryClient();

  // Track whether this is a retry (manual refetch after initial attempt)
  const [isRetrying, setIsRetrying] = useState(false);

  const {
    data: aaStatus,
    error,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: [...aaQueryKeys.all, "callback-status"] as const,
    queryFn: getAAStatus,
    enabled,
    retry: false, // We handle retries manually via the retry function
    staleTime: Infinity, // Don't auto-refetch since this is for callback handling
  });

  // Convert error to Error type (React Query uses unknown)
  const statusError = error
    ? error instanceof Error
      ? error
      : new Error(String(error))
    : null;

  // Clear isRetrying when fetch completes
  useEffect(() => {
    if (!isFetching && isRetrying) {
      setIsRetrying(false);
    }
  }, [isFetching, isRetrying]);

  // Retry function that uses React Query's refetch
  const retry = useCallback(() => {
    if (!enabled || isFetching) return;
    setIsRetrying(true);
    // Invalidate other AA status queries so they get fresh data after retry succeeds
    void refetch().then(() => {
      queryClient.invalidateQueries({ queryKey: aaQueryKeys.status() });
    });
  }, [enabled, isFetching, refetch, queryClient]);

  return {
    aaStatus: aaStatus ?? null,
    statusError,
    isFetching,
    isRetrying,
    retry,
  };
}

// ============================================================================
// useAACallback Hook (Composed)
// ============================================================================

/**
 * Main hook for processing AA callback from Saafe redirect.
 *
 * This is a composed hook that combines:
 * - `useAACallbackParams()` - URL parameter parsing
 * - `useAAStatusFetch()` - AA status fetching with retry
 * - Navigation utilities with type-safe routes
 *
 * **IMPORTANT: External Redirect Context**
 * This hook is designed to run after a fresh page load triggered by an
 * external redirect from Saafe. The callback parameters are parsed from
 * `window.location.search` on initial mount.
 *
 * @warning **Client-Side Navigation Limitation**
 * This hook reads URL parameters once on mount and will NOT react to
 * client-side navigation changes. This is intentional because AA callbacks
 * always come from external browser redirects.
 *
 * @param options - Hook configuration options
 * @returns Callback state, AA status, and navigation utilities
 *
 * @example
 * ```tsx
 * function AACallbackPage() {
 *   const {
 *     hasCallback,
 *     callbackResult,
 *     isSuccess,
 *     isError,
 *     isProcessing,
 *     isRetrying,
 *     aaStatus,
 *     errorMessage,
 *     navigateToPortfolio,
 *     retryStatusFetch,
 *   } = useAACallback();
 *
 *   if (!hasCallback) {
 *     return <Redirect to="/" />;
 *   }
 *
 *   if (isProcessing) {
 *     return <LoadingSpinner />;
 *   }
 *
 *   if (isError) {
 *     return (
 *       <ErrorScreen
 *         message={errorMessage}
 *         onRetry={retryStatusFetch}
 *         isRetrying={isRetrying}
 *       />
 *     );
 *   }
 *
 *   return (
 *     <SuccessScreen
 *       linkedAccounts={aaStatus?.linkedAccountsCount ?? callbackResult.linkedAccountsCount}
 *       onContinue={navigateToPortfolio}
 *     />
 *   );
 * }
 * ```
 */
export function useAACallback(
  options: UseAACallbackOptions = {}
): UseAACallbackReturn {
  const { fetchStatusOnSuccess = true, autoClearParams = false } = options;

  const navigate = useNavigate();

  // Track retry attempts for escalating error recovery UI
  const [retryCount, setRetryCount] = useState(0);

  // Compose the smaller hooks
  const { hasCallback, callbackResult, clearCallbackParams } =
    useAACallbackParams();

  // Determine if we should fetch status
  const shouldFetchStatus =
    hasCallback &&
    callbackResult.status === "success" &&
    fetchStatusOnSuccess;

  const { aaStatus, statusError, isFetching, isRetrying, retry } =
    useAAStatusFetch(shouldFetchStatus);

  // Compute the callback state
  const state = useMemo((): CallbackState => {
    if (!hasCallback) return "idle";

    if (callbackResult.status === "error") return "error";

    if (callbackResult.status === "success") {
      // If we're fetching status (and fetchStatusOnSuccess is true), we're processing
      if (fetchStatusOnSuccess && isFetching) return "processing";
      // Otherwise, we're done
      return "success";
    }

    return "idle";
  }, [hasCallback, callbackResult.status, fetchStatusOnSuccess, isFetching]);

  // Derived state
  const isSuccess = state === "success";
  const isError = state === "error";
  const isProcessing = state === "processing";
  const isIdle = state === "idle";

  // Error message
  const errorMessage = useMemo(() => {
    if (callbackResult.error) {
      return callbackResult.error;
    }
    if (statusError) {
      return getAAErrorMessage(statusError);
    }
    return null;
  }, [callbackResult.error, statusError]);

  // Auto-clear params if enabled
  useEffect(() => {
    if (autoClearParams && hasCallback && (isSuccess || isError)) {
      clearCallbackParams();
    }
  }, [autoClearParams, hasCallback, isSuccess, isError, clearCallbackParams]);

  // Navigate to portfolio/home
  const navigateToPortfolio = useCallback(() => {
    clearCallbackParams();
    navigate({ to: "/" });
  }, [clearCallbackParams, navigate]);

  // Navigate to a specific path (type-safe)
  const navigateTo = useCallback(
    (path: ValidRoutePath) => {
      clearCallbackParams();
      navigate({ to: path });
    },
    [clearCallbackParams, navigate]
  );

  // Wrap retry to track count
  const retryStatusFetch = useCallback(() => {
    setRetryCount((c) => c + 1);
    retry();
  }, [retry]);

  return {
    hasCallback,
    callbackResult,
    state,
    isSuccess,
    isError,
    isProcessing,
    isIdle,
    isRetrying,
    retryCount,
    aaStatus,
    statusError,
    errorMessage,
    clearCallbackParams,
    navigateToPortfolio,
    navigateTo,
    retryStatusFetch,
  };
}
